import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { Category, isAnyIssueProjectIgnoredByRule, isGlobalScope, isProjectScope } from '../../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import Flex from '../../../generic/Flex';

import EditPackage from './EditPackage';
import Export from './Export';
import Ignore from './Ignore';
import Unignore from './Unignore';
import Unlink from './Unlink';
import ViewPath from './ViewPath';

/**
 * The global issue action buttons behave slightly differently,
 * since the issue could technically be in multiple states in
 * various projects. Consider an issue which has been ignored
 * in one project but is active in another -- in this case we
 * would need to show both the Ignore and Unignore buttons.
 *
 * Another difference is the View Path button, which is hidden on
 * the global (and release group) issue pages, since that there
 * is no "source" or parent/user project to view the path from.
 */
const IssueDetailsActionButtonsGlobal: VoidFunctionComponent = () => {
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const activeCount = useSelector(IssuesV2Store.selectors.getActiveIssueCount);
  const ignoredCount = useSelector(IssuesV2Store.selectors.getIgnoredIssueCount);
  const ticketedCount = useSelector(IssuesV2Store.selectors.getUniqueTicketedIssueCount);
  const affectedProjectsCount = useSelector(IssuesV2Store.selectors.getAffectedProjectsCount);
  const canUserEditSourceProject = useSelector(IssuesV2Store.selectors.getCanUserEditSourceProject);

  if (!issueDetails) {
    return null;
  }

  return (
    <>
      {activeCount > 0 && (
        <Ignore id={issueDetails.id} />
      )}
      {ignoredCount > 0 && (
        <Unignore
          id={issueDetails.id}
          areAnyIgnoredByRule={isAnyIssueProjectIgnoredByRule(issueDetails.projects)}
        />
      )}
      {ticketedCount !== affectedProjectsCount && (
        <Export
          id={issueDetails.id}
          issue={issueDetails}
          jiraTicketCount={ticketedCount}
        />
      )}
      {ticketedCount > 0 && (
        <Unlink
          id={issueDetails.id}
          jiraTicketCount={ticketedCount}
        />
      )}
      {/* The Edit Package button is displayed for licensing issues, but only when the user is allowed to edit. */}
      {canUserEditSourceProject && category === Category.Licensing && (
        <EditPackage />
      )}
    </>
  );
};

/**
 * IssueDetailsActionButtons displays the buttons on the details page and
 * in the drawer which allow the user to take various actions,
 * such as ignore/unignore and ticket/unlink.
 */
const IssueDetailsActionButtons: VoidFunctionComponent = () => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const status = useSelector(IssuesV2Store.selectors.getIssueDetailsIssueStatus);
  const issueDetails = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const jiraTicket = useSelector(IssuesV2Store.selectors.getFirstJiraTicket);
  //  This is needed only in the project scope, to determine if there are project-level Jira settings to use.
  //  For global/release groups, we just use the global Jira settings.
  const project = useSelector(IssuesV2Store.selectors.getIssueDetailsFirstProject);
  const canUserEditSourceProject = useSelector(IssuesV2Store.selectors.getCanUserEditSourceProject);

  if (!issueDetails) {
    return null;
  }

  return (
    <>
      {status === 'active' && (
        <Ignore id={issueDetails.id} />
      )}
      {status === 'ignored' && (
        <Unignore
          id={issueDetails.id}
          areAnyIgnoredByRule={isAnyIssueProjectIgnoredByRule(issueDetails.projects)}
        />
      )}
      {jiraTicket ? (
        <Unlink
          id={issueDetails.id}
          jiraTicketURL={jiraTicket.url}
        />
      ) : (
        <Export
          id={issueDetails.id}
          issue={issueDetails}
          projectId={project?.id}
        />
      )}
      {isProjectScope(scope) && (
        <ViewPath />
      )}
      {/* The Edit Package button is displayed for licensing issues, but only when the user is allowed to edit. */}
      {canUserEditSourceProject && category === Category.Licensing && (
        <EditPackage />
      )}
    </>
  );
};

/**
 * Top-level switch component which determines if we render the
 * global issue action buttons or the regular project level
 * issue actions, as they display slightly differently.
 */
const IssueDetailsActionButtonsContainer: VoidFunctionComponent = () => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);

  return (
    <div className="margin-bottom-md">
      <Flex
        direction="row"
        justify="flex-start"
        gap="10px"
      >
        {isGlobalScope(scope)
          ? <IssueDetailsActionButtonsGlobal />
          : <IssueDetailsActionButtons />}
      </Flex>
    </div>
  );
};

export default IssueDetailsActionButtonsContainer;
